import type { FC } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import Animated from 'react-native-reanimated';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiLoyaltyCard from 'src/core/models/api/ApiLoyaltyCard';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useLoyaltyStore } from 'src/domain/stores/useLoyaltyStore';
import { Button, TertiaryButton } from 'src/presentation/components/molecules/Button';
import CardLabel from 'src/presentation/components/molecules/CardLabel';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { gridColumnWidth } from 'src/presentation/helpers/CardGrid';
import { useBenefitCta } from 'src/presentation/hooks/useBenefitCta';
import { useGridCardEntrance } from 'src/presentation/hooks/useGridCardEntrance';
import { usePressFeedback } from 'src/presentation/hooks/usePressFeedback';

interface Props {
	card: ApiLoyaltyCard;
	index: number;
}

const BenefitGridCard: FC<Props> = ({ card, index }: Props) => {
	const { width } = useWindowDimensions();
	const { openBenefit } = useLoyaltyStore();
	const { editorialLabel, redeem } = useBenefitCta(card);
	const entrance = useGridCardEntrance(index);
	const feedback = usePressFeedback();

	const isUsed = card.used === true;
	const showDetails = () => openBenefit(card);

	return (
		<Animated.View style={[styles.card, { width: gridColumnWidth(width) }, entrance]}>
			<Pressable
				accessibilityLabel={card.name}
				accessibilityRole="button"
				onPress={showDetails}
				onPressIn={feedback.onPressIn}
				onPressOut={feedback.onPressOut}
			>
				<Animated.View style={[styles.thumbnail, feedback.style]}>
					<ThermenImage
						contentFit="cover"
						source={card.image_thumb ? { uri: card.image_thumb } : null}
						style={styles.thumbnailImage}
					/>
					{card.sticker_text ? (
						<CardLabel icon={card.sticker_icon} size="small" style={styles.sticker} text={card.sticker_text} variant="dark" />
					) : null}
				</Animated.View>
			</Pressable>
			<View style={styles.body}>
				<Text style={styles.title}>{card.name}</Text>
				<View style={styles.actions}>
					{card.no_cta === true ? null : (
						<TertiaryButton
							buttonStyle={styles.action}
							disabled={isUsed}
							label={isUsed ? Translations.loyaltyBenefitUsed : (editorialLabel ?? Translations.loyaltyUseBenefit)}
							textStyle={styles.actionLabel}
							onPress={redeem}
						/>
					)}
					<Button
						backgroundColor={Colors.gray75}
						borderColor={Colors.gray200}
						buttonStyle={styles.action}
						iconColor={Colors.gray900}
						iconSize={Dimens.iconSize10}
						label={Translations.loyaltyMoreInfo}
						svgIcon={SvgIconType.tooltip}
						textColor={Colors.gray900}
						textStyle={styles.actionLabel}
						onPress={showDetails}
					/>
				</View>
			</View>
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	card: {
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.white,
		overflow: 'hidden',
	},
	thumbnail: {
		width: '100%',
		aspectRatio: Dimens.aspectRatio16By9,
		backgroundColor: Colors.gray300,
	},
	thumbnailImage: {
		width: '100%',
		height: '100%',
	},
	sticker: {
		left: Dimens.padding10,
		top: Dimens.padding10,
		marginRight: Dimens.padding10,
	},
	body: {
		flex: Dimens.flex1,
		justifyContent: 'space-between',
		gap: Dimens.gap16,
		padding: Dimens.padding12,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight20,
		color: Colors.gray900,
	},
	actions: {
		gap: Dimens.gap12,
	},
	action: {
		height: Dimens.buttonHeight36,
		paddingHorizontal: Dimens.padding12,
	},
	actionLabel: {
		textTransform: 'none',
	},
});

export default BenefitGridCard;
